// @figma file=pkCGby54zXzTsccXvHqzO6 node=3969:51446 name="Dropdown" set="my-taible-shadcn-studio" verified=2026-03-22

import * as DropdownMenuPrimitive from '@radix-ui/react-dropdown-menu';
import { CheckIcon, ChevronRightIcon, CircleIcon } from 'lucide-react';
import * as React from 'react';
import { cn } from '../../lib/utils';

// ─── DropdownMenu (root) ──────────────────────────────────────────────────────

/**
 * Contextual action menu triggered by a button, built on Radix
 * DropdownMenuPrimitive with full support for items, labels, separators,
 * checkbox items, radio items, and submenus.
 *
 * Built on `@radix-ui/react-dropdown-menu` with full ARIA compliance:
 * - Role: `menu` on the content, `menuitem` on each item
 * - Keyboard: `Enter`/`Space` to select, arrow keys to navigate,
 *   `Escape` to close, `Tab` to close and move focus
 * - Focus management: focus returns to the trigger on close
 *
 * @example
 * ```tsx
 * <DropdownMenu>
 *   <DropdownMenuTrigger asChild>
 *     <Button>Open menu</Button>
 *   </DropdownMenuTrigger>
 *   <DropdownMenuContent>
 *     <DropdownMenuLabel>My Account</DropdownMenuLabel>
 *     <DropdownMenuSeparator />
 *     <DropdownMenuItem>Profile</DropdownMenuItem>
 *     <DropdownMenuItem>Billing</DropdownMenuItem>
 *     <DropdownMenuSeparator />
 *     <DropdownMenuItem disabled>API (coming soon)</DropdownMenuItem>
 *   </DropdownMenuContent>
 * </DropdownMenu>
 * ```
 */

export interface DropdownMenuProps
  extends React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Root> {}

const DropdownMenu = DropdownMenuPrimitive.Root;
DropdownMenu.displayName = 'DropdownMenu';

// ─── DropdownMenuTrigger ──────────────────────────────────────────────────────

export interface DropdownMenuTriggerProps
  extends React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Trigger> {}

const DropdownMenuTrigger = React.forwardRef<
  React.ComponentRef<typeof DropdownMenuPrimitive.Trigger>,
  DropdownMenuTriggerProps
>(({ className, ...props }, ref) => (
  <DropdownMenuPrimitive.Trigger
    ref={ref}
    className={cn(
      // Remove default button outline — consumers supply their own trigger styling
      'focus-visible:outline-none',
      className,
    )}
    {...props}
  />
));
DropdownMenuTrigger.displayName = 'DropdownMenuTrigger';

// ─── DropdownMenuPortal ───────────────────────────────────────────────────────

export interface DropdownMenuPortalProps
  extends React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Portal> {}

const DropdownMenuPortal = DropdownMenuPrimitive.Portal;
DropdownMenuPortal.displayName = 'DropdownMenuPortal';

// ─── DropdownMenuContent ──────────────────────────────────────────────────────

export interface DropdownMenuContentProps
  extends React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Content> {
  /**
   * Distance in px between the trigger and the content panel.
   * Matches Figma — trigger sits 48px from the top of the Dropdown frame.
   * @default 4
   */
  sideOffset?: number;
}

const DropdownMenuContent = React.forwardRef<
  React.ComponentRef<typeof DropdownMenuPrimitive.Content>,
  DropdownMenuContentProps
>(({ className, sideOffset = 4, ...props }, ref) => (
  <DropdownMenuPortal>
    <DropdownMenuPrimitive.Content
      ref={ref}
      sideOffset={sideOffset}
      className={cn(
        'z-50 overflow-hidden',
        // Desktop
        'min-w-[8rem] w-56 rounded-[min(var(--radius),0.75rem)] border border-border bg-popover text-popover-foreground p-1 shadow-md',
        // Mobile/tablet — iOS card style
        'max-lg:w-auto max-lg:min-w-[220px] max-lg:rounded-2xl max-lg:border-0',
        'max-lg:bg-card/95 max-lg:backdrop-blur-xl max-lg:shadow-2xl max-lg:p-0',
        // Entry/exit animations
        'data-[state=open]:animate-in data-[state=closed]:animate-out',
        'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
        'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
        // Desktop — directional slide
        'data-[side=bottom]:slide-in-from-top-2',
        'data-[side=left]:slide-in-from-right-2',
        'data-[side=right]:slide-in-from-left-2',
        'data-[side=top]:slide-in-from-bottom-2',
        className,
      )}
      {...props}
    />
  </DropdownMenuPortal>
));
DropdownMenuContent.displayName = 'DropdownMenuContent';

// ─── DropdownMenuItem ─────────────────────────────────────────────────────────

export interface DropdownMenuItemProps
  extends React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Item> {
  /**
   * When `true`, shifts the item's content rightward to align with labelled
   * sections that have icons. Matches the Figma inset behaviour.
   */
  inset?: boolean;
}

const DropdownMenuItem = React.forwardRef<
  React.ComponentRef<typeof DropdownMenuPrimitive.Item>,
  DropdownMenuItemProps
>(({ className, inset, ...props }, ref) => (
  <DropdownMenuPrimitive.Item
    ref={ref}
    className={cn(
      'relative flex cursor-pointer select-none items-center outline-none',
      // Desktop
      'gap-2 rounded-sm px-2 py-1.5 text-sm',
      // Mobile/tablet — iOS sizing, full-width, no per-item radius
      'max-lg:rounded-none max-lg:px-4 max-lg:py-3.5 max-lg:text-[15px] max-lg:gap-3',
      // Dividers between siblings on mobile
      'max-lg:[&:not(:first-child)]:border-t max-lg:[&:not(:first-child)]:border-border/20',
      'text-foreground',
      'focus:bg-accent focus:text-accent-foreground',
      'data-[highlighted]:bg-accent data-[highlighted]:text-accent-foreground',
      'data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
      inset && 'pl-8',
      className,
    )}
    {...props}
  />
));
DropdownMenuItem.displayName = 'DropdownMenuItem';

// ─── DropdownMenuCheckboxItem ─────────────────────────────────────────────────

export interface DropdownMenuCheckboxItemProps
  extends React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.CheckboxItem> {}

const DropdownMenuCheckboxItem = React.forwardRef<
  React.ComponentRef<typeof DropdownMenuPrimitive.CheckboxItem>,
  DropdownMenuCheckboxItemProps
>(({ className, children, checked, ...props }, ref) => (
  <DropdownMenuPrimitive.CheckboxItem
    ref={ref}
    className={cn(
      'relative flex cursor-pointer select-none items-center outline-none',
      'rounded-sm py-1.5 pl-8 pr-2 text-sm',
      'max-lg:rounded-none max-lg:py-3.5 max-lg:pl-10 max-lg:pr-4 max-lg:text-[15px]',
      'max-lg:[&:not(:first-child)]:border-t max-lg:[&:not(:first-child)]:border-border/20',
      'text-foreground',
      'focus:bg-accent focus:text-accent-foreground',
      'data-[highlighted]:bg-accent data-[highlighted]:text-accent-foreground',
      'data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
      className,
    )}
    checked={checked}
    {...props}
  >
    {/* Check indicator — absolutely positioned in the left indent zone */}
    <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
      <DropdownMenuPrimitive.ItemIndicator>
        <CheckIcon className="h-4 w-4" aria-hidden="true" />
      </DropdownMenuPrimitive.ItemIndicator>
    </span>
    {children}
  </DropdownMenuPrimitive.CheckboxItem>
));
DropdownMenuCheckboxItem.displayName = 'DropdownMenuCheckboxItem';

// ─── DropdownMenuRadioGroup ───────────────────────────────────────────────────

export interface DropdownMenuRadioGroupProps
  extends React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.RadioGroup> {}

const DropdownMenuRadioGroup = React.forwardRef<
  React.ComponentRef<typeof DropdownMenuPrimitive.RadioGroup>,
  DropdownMenuRadioGroupProps
>(({ className, ...props }, ref) => (
  <DropdownMenuPrimitive.RadioGroup ref={ref} className={cn(className)} {...props} />
));
DropdownMenuRadioGroup.displayName = 'DropdownMenuRadioGroup';

// ─── DropdownMenuRadioItem ────────────────────────────────────────────────────

export interface DropdownMenuRadioItemProps
  extends React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.RadioItem> {}

const DropdownMenuRadioItem = React.forwardRef<
  React.ComponentRef<typeof DropdownMenuPrimitive.RadioItem>,
  DropdownMenuRadioItemProps
>(({ className, children, ...props }, ref) => (
  <DropdownMenuPrimitive.RadioItem
    ref={ref}
    className={cn(
      'relative flex cursor-pointer select-none items-center outline-none',
      'rounded-sm py-1.5 pl-8 pr-2 text-sm',
      'max-lg:rounded-none max-lg:py-3.5 max-lg:pl-10 max-lg:pr-4 max-lg:text-[15px]',
      'max-lg:[&:not(:first-child)]:border-t max-lg:[&:not(:first-child)]:border-border/20',
      'text-foreground',
      'focus:bg-accent focus:text-accent-foreground',
      'data-[highlighted]:bg-accent data-[highlighted]:text-accent-foreground',
      'data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
      className,
    )}
    {...props}
  >
    {/* Radio dot indicator — absolutely positioned in the left indent zone */}
    <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
      <DropdownMenuPrimitive.ItemIndicator>
        <CircleIcon className="h-2 w-2 fill-current" aria-hidden="true" />
      </DropdownMenuPrimitive.ItemIndicator>
    </span>
    {children}
  </DropdownMenuPrimitive.RadioItem>
));
DropdownMenuRadioItem.displayName = 'DropdownMenuRadioItem';

// ─── DropdownMenuLabel ────────────────────────────────────────────────────────

export interface DropdownMenuLabelProps
  extends React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Label> {
  /**
   * When `true`, shifts the label rightward to align with inset menu items.
   */
  inset?: boolean;
}

const DropdownMenuLabel = React.forwardRef<
  React.ComponentRef<typeof DropdownMenuPrimitive.Label>,
  DropdownMenuLabelProps
>(({ className, inset, ...props }, ref) => (
  <DropdownMenuPrimitive.Label
    ref={ref}
    className={cn(
      'px-2 py-1.5 text-sm font-medium text-foreground',
      'max-lg:px-4 max-lg:py-3 max-lg:text-xs max-lg:font-semibold max-lg:uppercase max-lg:tracking-wider max-lg:text-muted-foreground',
      'max-lg:[&:not(:first-child)]:border-t max-lg:[&:not(:first-child)]:border-border/20',
      inset && 'pl-8',
      className,
    )}
    {...props}
  />
));
DropdownMenuLabel.displayName = 'DropdownMenuLabel';

// ─── DropdownMenuSeparator ────────────────────────────────────────────────────

export interface DropdownMenuSeparatorProps
  extends React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Separator> {}

const DropdownMenuSeparator = React.forwardRef<
  React.ComponentRef<typeof DropdownMenuPrimitive.Separator>,
  DropdownMenuSeparatorProps
>(({ className, ...props }, ref) => (
  <DropdownMenuPrimitive.Separator
    ref={ref}
    className={cn(
      '-mx-1 my-1 h-px bg-border',
      // Mobile — no negative margins (container has no padding); use a taller gap
      'max-lg:mx-0 max-lg:my-0 max-lg:h-[6px] max-lg:bg-muted/80',
      className,
    )}
    {...props}
  />
));
DropdownMenuSeparator.displayName = 'DropdownMenuSeparator';

// ─── DropdownMenuSub ──────────────────────────────────────────────────────────

export interface DropdownMenuSubProps
  extends React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Sub> {}

const DropdownMenuSub = DropdownMenuPrimitive.Sub;
DropdownMenuSub.displayName = 'DropdownMenuSub';

// ─── DropdownMenuSubTrigger ───────────────────────────────────────────────────

export interface DropdownMenuSubTriggerProps
  extends React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.SubTrigger> {
  /** When `true`, shifts content rightward to align with icon-labelled sections. */
  inset?: boolean;
}

const DropdownMenuSubTrigger = React.forwardRef<
  React.ComponentRef<typeof DropdownMenuPrimitive.SubTrigger>,
  DropdownMenuSubTriggerProps
>(({ className, inset, children, ...props }, ref) => (
  <DropdownMenuPrimitive.SubTrigger
    ref={ref}
    className={cn(
      'flex cursor-default select-none items-center outline-none',
      'gap-2 rounded-sm px-2 py-1.5 text-sm',
      'max-lg:rounded-none max-lg:px-4 max-lg:py-3.5 max-lg:text-[15px] max-lg:gap-3',
      'max-lg:[&:not(:first-child)]:border-t max-lg:[&:not(:first-child)]:border-border/20',
      'text-foreground',
      'focus:bg-accent data-[state=open]:bg-accent',
      'data-[highlighted]:bg-accent',
      inset && 'pl-8',
      className,
    )}
    {...props}
  >
    {children}
    {/* Chevron — Figma: Lucide Icons / chevron-right on submenu items */}
    <ChevronRightIcon className="ml-auto h-4 w-4 text-muted-foreground" aria-hidden="true" />
  </DropdownMenuPrimitive.SubTrigger>
));
DropdownMenuSubTrigger.displayName = 'DropdownMenuSubTrigger';

// ─── DropdownMenuSubContent ───────────────────────────────────────────────────

export interface DropdownMenuSubContentProps
  extends React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.SubContent> {}

const DropdownMenuSubContent = React.forwardRef<
  React.ComponentRef<typeof DropdownMenuPrimitive.SubContent>,
  DropdownMenuSubContentProps
>(({ className, ...props }, ref) => (
  <DropdownMenuPrimitive.SubContent
    ref={ref}
    className={cn(
      'z-50 overflow-hidden',
      'min-w-[8rem] rounded-[min(var(--radius),0.75rem)] border border-border bg-popover text-popover-foreground p-1 shadow-md',
      'max-lg:min-w-[220px] max-lg:rounded-2xl max-lg:border-0',
      'max-lg:bg-card/95 max-lg:backdrop-blur-xl max-lg:shadow-2xl max-lg:p-0',
      'data-[state=open]:animate-in data-[state=closed]:animate-out',
      'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
      'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
      'data-[side=bottom]:slide-in-from-top-2',
      'data-[side=left]:slide-in-from-right-2',
      'data-[side=right]:slide-in-from-left-2',
      'data-[side=top]:slide-in-from-bottom-2',
      className,
    )}
    {...props}
  />
));
DropdownMenuSubContent.displayName = 'DropdownMenuSubContent';

// ─── DropdownMenuShortcut ─────────────────────────────────────────────────────

/**
 * Optional keyboard shortcut hint rendered at the far right of a DropdownMenuItem.
 *
 * @example
 * ```tsx
 * <DropdownMenuItem>
 *   Settings
 *   <DropdownMenuShortcut>⌘,</DropdownMenuShortcut>
 * </DropdownMenuItem>
 * ```
 */
export interface DropdownMenuShortcutProps extends React.HTMLAttributes<HTMLSpanElement> {}

const DropdownMenuShortcut = React.forwardRef<HTMLSpanElement, DropdownMenuShortcutProps>(
  ({ className, ...props }, ref) => (
    <span
      ref={ref}
      className={cn('ml-auto text-xs tracking-widest text-muted-foreground', className)}
      {...props}
    />
  ),
);
DropdownMenuShortcut.displayName = 'DropdownMenuShortcut';

// ─── Exports ──────────────────────────────────────────────────────────────────

export {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuPortal,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
};
